import React, { useState, useEffect } from "react";
import {
  Card,
  Collapse,
  Tag,
  Badge,
  Space,
  Button,
  Spin,
  Tooltip,
  Empty,
  Typography,
  Row,
  Col,
  Select,
  Dropdown,
  InputNumber,
  Input,
  Divider,
  Table,
} from "antd";
import {
  PrinterOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
  PlusOutlined,
  DownOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getInstructionsForJob,
  generateInstruction,
  updateInstruction,
} from "../../api/workInstructionsApi";
import WorkInstructionDetail from "./WorkInstructionDetail";

const { Panel } = Collapse;
const { Text } = Typography;

const STAGE_ICONS = {
  corrugation: "🔧",
  sheet_cutting: "✂️",
  pasting: "🔗",
  printing: "🖨️",
  casting: "⚙️",
  die_cutting: "⚙️",
  rotary: "🔩",
  joining: "📦",
  glueing: "📦",
  stitching: "📦",
  finishing: "📦",
  FINISHING: "📦",
};

const { Option } = Select;

// Normalize legacy die_cutting stage references to casting
const normalizeStageName = (name) => {
  if (!name) return name;
  if (name === "Die Cutting / Rotary" || name === "Die Cutting" || name === "Die cutting") return "Casting";
  return name;
};
const normalizeStageCode = (code) => {
  if (code === "die_cutting" || code === "rotary") return "casting";
  return code;
};
const normalizeInstructionType = (type) => {
  if (type === "DIE_CUTTING" || type === "ROTARY") return "CASTING";
  return type;
};

const WorkInstructionsPanel = ({ jobId, selectedMachines: selectedMachinesProp, onInstructionChange }) => {
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Parse selected machines to get stages with machines assigned
  const getStagesWithMachines = () => {
    let parsed = [];
    try {
      if (typeof selectedMachinesProp === "string") {
        parsed = JSON.parse(selectedMachinesProp);
      } else if (Array.isArray(selectedMachinesProp)) {
        parsed = selectedMachinesProp;
      }
    } catch {
      parsed = [];
    }
    return (parsed || []).filter((sm) => sm.machine_id);
  };

  const stagesWithMachines = getStagesWithMachines();

  useEffect(() => {
    if (jobId) {
      fetchInstructions();
    }
  }, [jobId]);

  const fetchInstructions = async () => {
    setLoading(true);
    try {
      const data = await getInstructionsForJob(jobId);
      setInstructions(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      toast.error("Failed to load work instructions");
    }
    setLoading(false);
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      await generateInstruction({ job_id: jobId, generate_all: true });
      toast.success("Work instructions generated successfully");
      fetchInstructions();
      if (onInstructionChange) onInstructionChange();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate instructions"
      );
    }
    setGenerating(false);
  };

  const handleRegenerate = async (stageCode) => {
    setGenerating(true);
    try {
      await generateInstruction({ job_id: jobId, stage_code: stageCode });
      toast.success("Instruction generated successfully");
      fetchInstructions();
      if (onInstructionChange) onInstructionChange();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate instruction"
      );
    }
    setGenerating(false);
  };

  const handlePrintAll = () => {
    window.open(`/job/${jobId}/instructions/print`, "_blank");
  };

  const handlePrintSingle = (instrId) => {
    window.open(`/work-instruction/${instrId}/print`, "_blank");
  };

  const handleStartEdit = (instruction) => {
    setEditingId(instruction.id);
    setEditData({
      content: JSON.parse(JSON.stringify(instruction.content)),
      materials: JSON.parse(JSON.stringify(instruction.materials || [])),
      notes: instruction.notes || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleSaveEdit = async (instrId) => {
    setSaving(true);
    try {
      await updateInstruction(instrId, editData);
      toast.success("Instruction updated successfully");
      setEditingId(null);
      setEditData(null);
      fetchInstructions();
      if (onInstructionChange) onInstructionChange();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update instruction");
    }
    setSaving(false);
  };

  const updateMaterialQty = (idx, newQty) => {
    const updated = [...editData.materials];
    updated[idx] = { ...updated[idx], quantity: newQty };
    setEditData({ ...editData, materials: updated });
  };

  const updateMaterialName = (idx, newName) => {
    const updated = [...editData.materials];
    updated[idx] = { ...updated[idx], name: newName };
    setEditData({ ...editData, materials: updated });
  };

  const getStageIcon = (stageCode) => STAGE_ICONS[stageCode] || "📋";

  if (loading) {
    return (
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Work Instructions</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Loading work instructions..." />
        </div>
      </Card>
    );
  }

  const generateDropdownItems = {
    items: stagesWithMachines.map((sm) => ({
      key: sm.stage_code,
      label: `${normalizeStageName(sm.stage_name)} (${sm.machine_code || "No machine"})`,
      icon: <span style={{ fontSize: 16 }}>{getStageIcon(normalizeStageCode(sm.stage_code))}</span>,
    })),
    onClick: ({ key }) => handleRegenerate(key),
  };

  if (instructions.length === 0) {
    return (
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Work Instructions</span>
          </Space>
        }
        extra={
          <Space>
            {stagesWithMachines.length > 0 && (
              <Dropdown menu={generateDropdownItems} disabled={generating}>
                <Button icon={<PlusOutlined />} loading={generating}>
                  Generate for Stage <DownOutlined />
                </Button>
              </Dropdown>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleGenerateAll}
              loading={generating}
            >
              Generate All
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            stagesWithMachines.length === 0
              ? "No machines selected. Please select machines first."
              : "No work instructions generated yet"
          }
        >
          <Space>
            {stagesWithMachines.length > 0 && (
              <Dropdown menu={generateDropdownItems} disabled={generating}>
                <Button icon={<PlusOutlined />} loading={generating}>
                  Generate for Stage <DownOutlined />
                </Button>
              </Dropdown>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleGenerateAll}
              loading={generating}
              disabled={stagesWithMachines.length === 0}
            >
              Generate All
            </Button>
          </Space>
        </Empty>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          <span>Work Instructions</span>
          <Badge
            count={instructions.length}
            style={{ backgroundColor: "#52c41a" }}
          />
        </Space>
      }
      extra={
        <Space>
          {stagesWithMachines.length > 0 && (
            <Dropdown menu={generateDropdownItems} disabled={generating}>
              <Button icon={<PlusOutlined />} loading={generating}>
                Generate for Stage <DownOutlined />
              </Button>
            </Dropdown>
          )}
          <Button
            icon={<ReloadOutlined spin={generating} />}
            onClick={handleGenerateAll}
            loading={generating}
          >
            Regenerate All
          </Button>
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={handlePrintAll}
          >
            Print All
          </Button>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Collapse accordion>
        {instructions.map((instruction, index) => (
          <Panel
            key={instruction.id}
            header={
              <Space>
                <span style={{ fontSize: 20 }}>
                  {getStageIcon(normalizeStageCode(instruction.stage_code))}
                </span>
                <strong>
                  Stage {index + 1}: {normalizeStageName(instruction.stage_name)}
                </strong>
                <Tag>{normalizeInstructionType(instruction.instruction_type)}</Tag>
                <Tag
                  color={
                    instruction.status === "COMPLETED" ? "green" : "blue"
                  }
                >
                  {instruction.status || "PENDING"}
                </Tag>
                {instruction.status === "COMPLETED" && (
                  <Tooltip
                    title={`Completed ${
                      instruction.completed_at
                        ? new Date(instruction.completed_at).toLocaleString()
                        : ""
                    }`}
                  >
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  </Tooltip>
                )}
              </Space>
            }
            extra={
              <Space onClick={(e) => e.stopPropagation()}>
                {editingId === instruction.id ? (
                  <>
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={handleCancelEdit}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => handleSaveEdit(instruction.id)}
                      loading={saving}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleStartEdit(instruction)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => setSelectedInstruction(instruction)}
                    >
                      Full View
                    </Button>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSingle(instruction.id)}
                    >
                      Print
                    </Button>
                  </>
                )}
              </Space>
            }
          >
            <InstructionSummary
              instruction={instruction}
              isEditing={editingId === instruction.id}
              editData={editData}
              onUpdateMaterialQty={updateMaterialQty}
              onUpdateMaterialName={updateMaterialName}
              onUpdateNotes={(notes) => setEditData({ ...editData, notes })}
            />
          </Panel>
        ))}
      </Collapse>

      {/* Full View Modal */}
      {selectedInstruction && (
        <WorkInstructionDetail
          instruction={selectedInstruction}
          onClose={() => setSelectedInstruction(null)}
        />
      )}
    </Card>
  );
};

/**
 * Summary view rendered inside each collapsed panel.
 * Supports editing mode for materials and notes.
 */
const InstructionSummary = ({
  instruction,
  isEditing,
  editData,
  onUpdateMaterialQty,
  onUpdateMaterialName,
  onUpdateNotes,
}) => {
  const { content, materials: origMaterials } = instruction;
  const materials = isEditing ? editData?.materials : origMaterials;

  if (!content) return <div>No content available</div>;

  return (
    <div>
      {/* Header Info */}
      {content.header && (
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <Text type="secondary">Item:</Text>{" "}
            <Text strong>{content.header.item_name}</Text>
          </Col>
          <Col span={10}>
            <Text type="secondary">Qty:</Text>{" "}
            <Text strong>
              {(content.header.box_quantity || content.header.quantity || 0).toLocaleString()} boxes
            </Text>
            {content.header.sheet_quantity && content.header.sheets_per_box > 1 && content.output_items && (
              <>
                <Divider type="vertical" />
                <Text type="secondary">Sheets:</Text>{" "}
                <Text strong>{content.header.sheet_quantity.toLocaleString()}</Text>
                <Text type="secondary"> (N={content.header.sheets_per_box})</Text>
              </>
            )}
          </Col>
          <Col span={6}>
            <Text type="secondary">Ply:</Text>{" "}
            <Text strong>{content.header.ply || "-"}</Text>
          </Col>
        </Row>
      )}

      {/* Output Info - Table format for pasting/casting/joining, text for others */}
      {content.output && !content.output_items && (
        content.output.sheet_count || content.output.output_boxes ? (
          // Pasting / Casting output - bold tabular format
          <div style={{ marginBottom: 12 }}>
            <Divider orientation="left" plain><Text strong>Output</Text></Divider>
            <Table
              dataSource={[{ key: 1, output: content.output.type, qty: content.output.output_boxes || content.output.sheet_count }]}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Output",
                  dataIndex: "output",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val}</Text>,
                },
                {
                  title: "Quantity",
                  dataIndex: "qty",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val?.toLocaleString()} {content.output.output_boxes ? "boxes" : "sheets"}</Text>,
                },
              ]}
            />
          </div>
        ) : content.output.total_impressions ? (
          // After-box printing output - boxes, impressions, marking
          <div style={{ marginBottom: 12 }}>
            <Divider orientation="left" plain><Text strong>Output</Text></Divider>
            <Table
              dataSource={[{ 
                key: 1, 
                boxes: content.output.box_count,
                impressions: content.output.total_impressions,
              }]}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Qty of Boxes",
                  dataIndex: "boxes",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val?.toLocaleString()}</Text>,
                },
                {
                  title: `Impressions (${content.output.impressions_per_box || 1} sides)`,
                  dataIndex: "impressions",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val?.toLocaleString()}</Text>,
                },
                {
                  title: "Marking / Artwork",
                  render: () => content.marking_pdf_url
                    ? <a href={content.marking_pdf_url} target="_blank" rel="noreferrer"><Button size="small" type="link">View PDF</Button></a>
                    : <Tag color="red">Missing</Tag>,
                },
              ]}
            />
          </div>
        ) : content.output.box_count && content.output.joins_per_box ? (
          // Joining output - boxes with joins count
          <div style={{ marginBottom: 12 }}>
            <Divider orientation="left" plain><Text strong>Output</Text></Divider>
            <Table
              dataSource={[{ 
                key: 1, 
                output: content.output.type, 
                boxes: content.output.box_count,
                joins: content.output.joins_per_box,
              }]}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Output",
                  dataIndex: "output",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val}</Text>,
                },
                {
                  title: "Qty of Boxes",
                  dataIndex: "boxes",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val?.toLocaleString()}</Text>,
                },
                {
                  title: "Joins per Box (N)",
                  dataIndex: "joins",
                  render: (val) => <Text strong style={{ fontSize: 15 }}>{val}</Text>,
                },
              ]}
            />
          </div>
        ) : (
          // Other stages - simple text output
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Output:</Text>{" "}
            <Text strong>{content.output.type}</Text>
            {content.output.roll_size && (
              <Text> — RS: {content.output.roll_size}" × CS: {content.output.cutting_size}"</Text>
            )}
            {content.output.roll_width && !content.output.roll_size && (
              <Text> — Roll Width: {content.output.roll_width}"</Text>
            )}
            {content.output.sheet_size && (
              <Text> — {content.output.sheet_size}</Text>
            )}
          </div>
        )
      )}

      {/* Output Items (sheet cutter - shows 2-ply sheets + top paper separately) */}
      {content.output_items && content.output_items.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain><Text strong>Output Needed</Text></Divider>
          <Table
            dataSource={content.output_items}
            pagination={false}
            size="small"
            rowKey={(_, i) => i}
            columns={[
              {
                title: "#",
                width: 40,
                render: (_, __, idx) => idx + 1,
              },
              {
                title: "Item",
                render: (_, item) => (
                  <span>
                    <strong>{item.label}</strong>
                    {item.material && <Text type="secondary"> ({item.material})</Text>}
                  </span>
                ),
              },
              {
                title: "Size",
                dataIndex: "size",
              },
              {
                title: "Quantity",
                render: (_, item) => <strong>{item.quantity?.toLocaleString()}</strong>,
              },
              {
                title: "Weight",
                render: (_, item) => <strong>{item.weight_kg} kg</strong>,
              },
              {
                title: "Stage",
                render: (_, item) => item.handled_at ? (
                  <Tag color={item.handled_at === "SHEET_CUTTING" ? "green" : "orange"}>
                    {item.handled_at === "SHEET_CUTTING" ? "Here" : "→ Pasting"}
                  </Tag>
                ) : <Tag color="green">Here</Tag>,
              },
            ]}
          />
        </div>
      )}

      {/* Cutting Specs (sheet cutter) */}
      {content.cutting_specs && (
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={12}>
            <Text type="secondary">Roll Size (RS):</Text>{" "}
            <Text strong>{content.cutting_specs.roll_size}"</Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">Cutting Size (CS):</Text>{" "}
            <Text strong>{content.cutting_specs.cutting_size}"</Text>
          </Col>
        </Row>
      )}

      {/* Weight Summary (sheet cutter specific) */}
      {content.weight_summary && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain><Text strong>Weight Summary</Text></Divider>
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary">2-Ply Sheets ({content.weight_summary.two_ply_sheet_qty?.toLocaleString()}):</Text><br />
              <Text strong>{content.weight_summary.two_ply_sheet_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Top Paper ({content.weight_summary.top_paper_qty?.toLocaleString()}):</Text><br />
              <Text strong>{content.weight_summary.top_paper_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Total Weight:</Text><br />
              <Text strong>{content.weight_summary.total_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Expected (Box Wt × Qty):</Text><br />
              <Text strong>
                {content.weight_summary.expected_net_weight_kg} kg
              </Text>
              {content.weight_summary.box_weight_kg > 0 && (
                <Text type="secondary"> ({content.weight_summary.box_weight_kg} kg × {content.header?.box_quantity?.toLocaleString()})</Text>
              )}
            </Col>
          </Row>
          <div style={{ marginTop: 8 }}>
            <Tag color={content.weight_summary.verification === "✅ MATCH" ? "green" : "warning"}>
              {content.weight_summary.verification}
            </Tag>
            {content.weight_summary.weight_difference_kg !== 0 && (
              <Text type="secondary"> Difference: {content.weight_summary.weight_difference_kg > 0 ? "+" : ""}{content.weight_summary.weight_difference_kg} kg</Text>
            )}
          </div>
        </div>
      )}

      {/* Machine Info */}
      {content.assigned_machine && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Machine:</Text>{" "}
          <Tag color="blue">
            {content.assigned_machine.machine_code} ({content.assigned_machine.machine_name})
          </Tag>
          {content.assigned_machine.machine_subtype && (
            <Tag color="geekblue">
              {content.assigned_machine.machine_subtype.replace(/_/g, " ").toUpperCase()}
            </Tag>
          )}
          {content.assigned_machine.is_outsourced && (
            <Tag color="orange">Outsourced — {content.assigned_machine.vendor_name}</Tag>
          )}
        </div>
      )}

      {/* Top Layer Note (corrugation specific) */}
      {content.top_layer_note && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 4 }}>
          <Text type="warning" strong>⚠ {content.top_layer_note.message}</Text>
        </div>
      )}

      {/* Corrugation Output */}
      {content.corrugation_output && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain>
            <Text strong>2-Ply Sheet Output</Text>
          </Divider>
          <Row gutter={16} style={{ padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
            <Col span={6}>
              <Text type="secondary">Liner:</Text><br />
              <Text strong>{content.corrugation_output.liner.material} {content.corrugation_output.liner.gsm} GSM</Text><br />
              <Text>{content.corrugation_output.liner.total_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Flute:</Text><br />
              <Text strong>{content.corrugation_output.flute.material} {content.corrugation_output.flute.gsm} GSM</Text><br />
              <Text>FF: {content.corrugation_output.flute.ff} — {content.corrugation_output.flute.total_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Net Weight:</Text><br />
              <Text strong style={{ fontSize: 16 }}>{content.corrugation_output.net_weight_kg} kg</Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Roll Width:</Text><br />
              <Text strong>{content.output?.roll_width}"</Text>
            </Col>
          </Row>
        </div>
      )}

      {/* Pasting Passes - hidden for now, data still calculated in backend
      {content.pasting_passes && content.pasting_passes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain><Text strong>Pasting Passes</Text></Divider>
          {content.pasting_passes.map((pass) => (
            <div key={pass.pass} style={{ marginBottom: 8, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
              <Text strong>Pass {pass.pass}: </Text>
              <Text>{pass.description}</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Input: </Text>
                {pass.input.map((inp, i) => (
                  <Tag key={i} color="blue">{inp}</Tag>
                ))}
                <Text type="secondary"> → Output: </Text>
                <Tag color="green">{pass.output}</Tag>
              </div>
            </div>
          ))}
        </div>
      )}
      */}

      {/* Top Paper - only for non-pasting stages (pasting output is already shown above) */}
      {content.top_paper && !content.output?.sheet_count && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
          <Text strong>📄 Top Paper: </Text>
          <Tag color="green">{content.top_paper.material} {content.top_paper.gsm} GSM</Tag>
          <Text> — Weight: <strong>{content.top_paper.weight_kg} kg</strong></Text>
          {content.top_paper.bf && <Text> (BF: {content.top_paper.bf})</Text>}
          <Text type="secondary"> — {content.top_paper.sheets?.toLocaleString()} sheets</Text>
        </div>
      )}

      {/* Planning Notes */}
      {content.planning_notes && content.planning_notes.length > 0 && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 4 }}>
          <Text strong>📋 Planning Notes:</Text>
          <ul style={{ marginTop: 4, marginBottom: 0 }}>
            {content.planning_notes.map((note, idx) => (
              <li key={idx}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Layer Configuration - only for non-pasting stages */}
      {content.layer_configuration && !content.output?.sheet_count && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain><Text strong>Layer Config</Text></Divider>
          <Space size={4} wrap>
            {content.layer_configuration.map((l, idx) => (
              <Tag
                key={idx}
                color={l.processed_in === "PASTING" ? "orange" : "green"}
              >
                L{l.layer}: {l.material} {l.gsm} GSM ({l.type})
                {l.ff ? ` FF:${l.ff}` : ""}
                {l.processed_in === "PASTING" ? " → Pasting" : ""}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      {/* Machine Settings */}
      {content.machine_settings && !content.corrugation_passes && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" strong>Machine Settings: </Text>
          {Object.entries(content.machine_settings).map(([key, value]) => (
            <Tag key={key}>{key.replace(/_/g, " ")}: {String(value)}</Tag>
          ))}
        </div>
      )}

      {/* Materials — editable in edit mode */}
      {materials && materials.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Divider orientation="left" plain><Text strong>Materials</Text></Divider>
          {isEditing ? (
            <Table
              dataSource={materials}
              pagination={false}
              size="small"
              rowKey={(_, i) => i}
              columns={[
                {
                  title: "Material",
                  dataIndex: "name",
                  render: (name, _, idx) => (
                    <Input
                      size="small"
                      value={name}
                      onChange={(e) => onUpdateMaterialName(idx, e.target.value)}
                      style={{ width: "100%" }}
                    />
                  ),
                },
                {
                  title: "Qty",
                  dataIndex: "quantity",
                  width: 120,
                  render: (qty, _, idx) => (
                    <InputNumber
                      size="small"
                      value={qty}
                      min={0}
                      precision={3}
                      onChange={(val) => onUpdateMaterialQty(idx, val)}
                      style={{ width: "100%" }}
                    />
                  ),
                },
                {
                  title: "Unit",
                  dataIndex: "unit",
                  width: 60,
                },
              ]}
            />
          ) : (
            <Space size={4} wrap>
              {materials.map((m, idx) => (
                <Tag key={idx} color="geekblue">
                  {m.name}: {m.quantity} {m.unit}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      )}

      {/* Notes — editable */}
      {isEditing && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" strong>Notes:</Text>
          <Input.TextArea
            rows={2}
            value={editData?.notes || ""}
            onChange={(e) => onUpdateNotes(e.target.value)}
            placeholder="Add notes for this instruction..."
            style={{ marginTop: 4 }}
          />
        </div>
      )}
      {!isEditing && instruction.notes && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">Notes:</Text> <Text>{instruction.notes}</Text>
        </div>
      )}
    </div>
  );
};

export default WorkInstructionsPanel;
